import React, { useState, useEffect, useContext } from "react";
import { ThemeContext, PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";
import {
  CHARTS,
  EXPANDED_CHART,
  EXPORT_PDF,
  GO_BACK,
  INTENSITY_LEVEL,
  OFF_DARK_MODE_EXPORT,
} from "../../constants/DashboardTranslation";

import { useHistory, useLocation } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import { parse, subDays, parseISO } from "date-fns";
import Form from "react-bootstrap/Form";

import theme from "../../index.scss";
import ComponentWrapper from "../Dashboard/ComponentWrapper";
import LineChartOptions from "../Dashboard/LineChartOptions";
import SubmitButton from "../UIComponents/SubmitButton";
import ModalPdf from "../Dashboard/ModalPdf";
import DoughnutTrigger from "./DoughnutTrigger";
import DoughnutMedical from "./DoughnutMedical";
import TriggerBarChart from "./TriggerBarChart";
import MedicalBarChart from "./MedicalBarChart";
import FeelingBar from "./FeelingBar";
import PillButton from "../UIComponents/PillButton";

import fillInDays from "../../lib/fillInDates";
import getDaysAgo from "../../lib/getDaysAgo";
import triggerWithSetVal from "../../lib/triggerWithSetVal";

import {
  getAdditionalNote,
  getAnotherTPOrPatientTrackedData,
  getConnectedPatientOfTPData,
} from "../../api/TBIRequests";


const ExpandedChart = ({
  user,
  userIdForTrackedData,
  error,
  topSymptoms,
  data,
  retry,
}) => {
  const location = useLocation();
  const history = useHistory();
  const days = location.state?.days || 30;

  const { dark } = useContext(ThemeContext);
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const tSymptom = (factor) => {
    const categories = ["cognitive","sleep","emotional","physical","speech","vision",
      "wellness","travel","screen_time","environmental","dietary","situational","other"];
    const safe = (factor || "").trim().toLowerCase()
      .replace(/ /g,"_").replace(/\//g,"_").replace(/,/g,"")
      .replace(/:/g,"").replace(/'/g,"").replace(/[()]/g,"").replace(/-/g,"_")
      .substring(0, 60);
    for (const cat of categories) {
      const symKey = `symptom.${cat}.${safe}`;
      const symT = t(symKey);
      if (symT !== symKey) return symT;
      const trigKey = `trigger.${cat}.${safe}`;
      const trigT = t(trigKey);
      if (trigT !== trigKey) return trigT;
    }
    return factor;
  };

  const tDay = (dayName) => {
    const key = `days.${(dayName || "").toLowerCase()}`;
    const translated = t(key);
    return translated !== key ? translated : dayName;
  };

  const tDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const dayNames = ["sun","mon","tue","wed","thu","fri","sat"];
    const monthNames = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
    const day = t(`days.short.${dayNames[d.getDay()]}`);
    const month = t(`months.short.${monthNames[d.getMonth()]}`);
    const dateNum = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${day} ${month} ${dateNum} ${year}`;
  };

  const tooltipCallbacks = {
    title: (items) => tDay(items[0]?.xLabel || ""),
    label: (item, data) => {
      const pointData = data.datasets[item.datasetIndex].data[item.index].y;
      if (pointData) {
        if (typeof pointData.y !== "number") return pointData.y?.desc;
        const desc = pointData.desc;
        if (typeof desc === "string" && desc) {
          return desc.split(",").map((d) => tSymptom(d.trim())).join(", ");
        }
        return desc;
      }
    },
  };

  const [assignedDays, setAssignedDays] = useState(days);
  const [additionalNotes, setAdditionalNotes] = useState([]);
  const [newData2, setNewData2] = useState({});
  const [anotherNewData2, setAnotherNewData2] = useState({});
  const [triggerByDays, setTriggerByDays] = useState([]);
  const [anotherTriggerByDays, setAnotherTriggerByDays] = useState([]);
  const [showLineModal, setShowLineModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [splitView, setSplitView] = useState(false);
  const [TPOrPatientTrackedData, setTPOrPatientTrackedData] = useState([]);
  const [otherUserInfo, setOtherUserInfo] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [TPList, setTPList] = useState([]);

  const handleSubmit = async (selectedId) => {
    try {
      let params = {
        patientId: patient.patientId,
        curProviderId: user.userId,
        selectedId: selectedId,
      };
      if (user.userType === "provider") {
        if (userIdForTrackedData) {
          params.userId = userIdForTrackedData;
        }
      }

      const { data } = await getAnotherTPOrPatientTrackedData(params); // If no userIdForTrackedData, it is a provider and it's a patient user being clicked on the patient control page

      setTPOrPatientTrackedData(data.result);
      setOtherUserInfo(data.otherUserInfo[0]);
      setSplitView(true);
      setErrorMessage("");
    } catch (error) {
      console.error(error);
      setErrorMessage(t("expandedchart.error"));
    }
  };

  const getData = async () => {
    try {
      let params = { patientId: patient.patientId };
      let data;
      if (user.userType === "provider") {
        params.curProviderId = user.userId;
        if (userIdForTrackedData) {
          params.userId = userIdForTrackedData;
        }
        ({ data } = await getAnotherTPOrPatientTrackedData(params)); // If no userIdForTrackedData, it is a provider and it's a patient user being clicked on the patient control page
      } else {
        // Show an error or simply do nothing
        setErrorMessage("Only providers can use this feature.");
        return;
      }

      // If no comparison then message is shown; else comparison is shown
      if (data.message) {
        // Handle error response
        if (data.TPList) {
          setTPList(data.TPList);
        }
        setErrorMessage(data.message);
      } else {
        setTPOrPatientTrackedData(data.result);
        setOtherUserInfo(data.otherUserInfo[0]);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(t("expandedchart.error"));
    }
  };

  const handleSplitViewShow = async () => {
    if (!splitView) {
      try {
        await getData();
      } catch (error) {
        console.error(error);
      }
    }
    setSplitView((prev) => !prev);
  };

  const anotherMedicalData = TPOrPatientTrackedData?.filter(
    (obj) => obj.category !== "SDOH"
  ).filter(
    (obj) =>
      parse(obj.symptom_date.slice(0, 10), "yyyy-MM-dd", new Date()) >
      getDaysAgo(assignedDays)
  );

  const anotherTriggerData = TPOrPatientTrackedData?.filter(
    (obj) => obj.category == "SDOH"
  ).filter(
    (obj) =>
      parse(obj.symptom_date.slice(0, 10), "yyyy-MM-dd", new Date()) >
      getDaysAgo(assignedDays)
  );

  const medicalData = data
    ?.filter((obj) => obj.category !== "SDOH")
    .filter(
      (obj) =>
        parse(obj.symptom_date.slice(0, 10), "yyyy-MM-dd", new Date()) >
        getDaysAgo(assignedDays)
    );

  const triggerData = data
    ?.filter((obj) => obj.category == "SDOH")
    .filter(
      (obj) =>
        parse(obj.symptom_date.slice(0, 10), "yyyy-MM-dd", new Date()) >
        getDaysAgo(assignedDays)
    );

  const colors = [
    theme.chart1,
    theme.chart2,
    theme.chart3,
    theme.chart4,
    theme.chart5,
    theme.chart6,
    theme.chart7,
    theme.chart8,
    theme.chart9,
    theme.chart10,
  ];
  const handleLineModalClose = () => setShowLineModal(false);
  const handleLineModalShow = () => setShowLineModal(true);

  useEffect(() => {
    getAdditionalNote({
      patientId: patient?.patientId,
      therapistId: user.userType === "Therapist" ? user.userId : null,
    })
      .then(({ data }) => {
        setAdditionalNotes(data);
      })
      .catch((e) => console.log("getAdditionalNotesError", e));
  }, []);

  let filterDataByDays = (dataset) => {
    const datasetConvertedToChartData = dataset.map((item) => {
      return {
        // CURRENT BUG, Must remove time stamp or labels do not work correctly
        x: item.symptom_date.slice(0, 10),
        y: item.severity,
        desc: item.description,
      };
    });

    const filteredByDays = datasetConvertedToChartData.filter(
      (item) =>
        parse(item.x, "yyyy-MM-dd", new Date()) > getDaysAgo(assignedDays)
    );
    return filteredByDays;
  };

  let datasetArr = topSymptoms.map((symptom) => {
    return data.filter(({ factor }) => factor === symptom.factor);
  });

  datasetArr.push(triggerByDays);

  let newData = {
    datasets: [],
    notes: additionalNotes,
  };
  let { datasets } = newData;

  datasetArr.forEach((dataset, index) => {
    const isLastDataset = index === datasetArr.length - 1;
    const datasetData = filterDataByDays(dataset);

    datasets.push({
      label: isLastDataset
        ? t("expandedchart.trigger")
        : tSymptom(topSymptoms[index].factor),
      data: datasetData,
      pointStyle: isLastDataset ? "triangle" : undefined,
      type: isLastDataset ? "scatter" : undefined,
      backgroundColor: isLastDataset ? theme.primary : colors[index],
      borderColor: isLastDataset ? theme.primary : colors[index],
      borderWidth: 1,
      fill: isLastDataset ? false : undefined,
      hoverBackgroundColor: isLastDataset ? theme.primary : colors[index],
      hoverBorderColor: "white",
    });
  });

  let ChartDataset = fillInDays(datasets, assignedDays);
  newData.datasets = ChartDataset;

  //data for compare
  let datasetArrC = topSymptoms.map((symptom) => {
    return TPOrPatientTrackedData.filter(
      ({ factor }) => factor === symptom.factor
    );
  });

  datasetArrC.push(anotherTriggerByDays);

  let newDataC = {
    datasets: [],
    notes: additionalNotes,
  };

  datasetArrC.forEach((dataset, index) => {
    const isLastDataset = index === datasetArrC.length - 1;
    const datasetData = filterDataByDays(dataset);

    newDataC.datasets.push({
      label: isLastDataset
        ? t("expandedchart.trigger")
        : tSymptom(topSymptoms[index].factor),
      data: datasetData,
      pointStyle: isLastDataset ? "triangle" : undefined,
      type: isLastDataset ? "scatter" : undefined,
      backgroundColor: isLastDataset ? theme.primary : colors[index],
      borderColor: isLastDataset ? theme.primary : colors[index],
      borderWidth: 1,
      fill: isLastDataset ? false : undefined,
      hoverBackgroundColor: isLastDataset ? theme.primary : colors[index],
      hoverBorderColor: "white",
    });
  });

  let ChartDatasetC = fillInDays(newDataC.datasets, assignedDays);
  newDataC.datasets = ChartDatasetC;

  useEffect(() => {
    // Previous Implementation: filters by top symptoms (top symptoms ACTUALLY referring to unpaused symptoms)
    // Current Implementation: looks at all symptoms and filters based on time frame

    // datasetArr = topSymptoms.map((symptom) => {
    //   return data.filter(({ factor }) => factor === symptom.factor);
    // });

    const startDate = subDays(new Date(), assignedDays);
    const recentMedical = data
      .filter((item) => item.category === "medical")
      .filter((item) => {
        const d = parseISO(item.symptom_date);
        return d >= startDate;
      });

    const factors = Array.from(
      new Set(recentMedical.map((item) => item.factor))
    );

    datasetArr = factors.map((factor) =>
      recentMedical.filter((item) => item.factor === factor)
    );

    const triggerList = triggerWithSetVal(data);
    const triggerByDays = triggerList.filter(
      (obj) =>
        parse(obj.symptom_date.slice(0, 10), "yyyy-MM-dd", new Date()) >
        getDaysAgo(assignedDays)
    );
    setTriggerByDays(triggerByDays);
    datasetArr.push(triggerList);

    datasets = [];
    datasetArr.forEach((dataset, index) => {
      const isLastDataset = index === datasetArr.length - 1;
      const datasetData = filterDataByDays(dataset);

      datasets.push({
        label: isLastDataset
          ? t("expandedchart.trigger")
          : tSymptom(factors[index]),
        data: datasetData,
        pointStyle: isLastDataset ? "triangle" : undefined,
        type: isLastDataset ? "scatter" : undefined,
        backgroundColor: isLastDataset ? theme.primary : colors[index],
        borderColor: isLastDataset ? theme.primary : colors[index],
        borderWidth: 1,
        fill: false,
        hoverBackgroundColor: isLastDataset ? theme.primary : colors[index],
        hoverBorderColor: "white",
      });
    });

    ChartDataset = fillInDays(datasets, assignedDays);
    const firstDay = getDaysAgo(assignedDays - 1);
    const formattedStartDate = tDate(firstDay);
    setStartDate(formattedStartDate);

    newData.datasets = ChartDataset;
    setNewData2(newData);

    // data for comparison

    // datasetArrC = topSymptoms.map((symptom) => {
    //   return TPOrPatientTrackedData.filter(
    //     ({ factor }) => factor === symptom.factor
    //   );
    // });

    const recentMedicalC = TPOrPatientTrackedData.filter(
      (item) => item.category === "medical"
    ).filter((item) => {
      const d = parseISO(item.symptom_date);
      return d >= startDate;
    });

    const factorsC = Array.from(
      new Set(recentMedicalC.map((item) => item.factor))
    );

    datasetArrC = factorsC.map((factor) =>
      recentMedicalC.filter((item) => item.factor === factor)
    );

    const triggerListC = triggerWithSetVal(TPOrPatientTrackedData);
    const anotherTriggerByDays = triggerListC.filter(
      (obj) =>
        parse(obj.symptom_date.slice(0, 10), "yyyy-MM-dd", new Date()) >
        getDaysAgo(assignedDays)
    );
    setAnotherTriggerByDays(anotherTriggerByDays);
    datasetArrC.push(triggerListC);

    newDataC.datasets = [];
    datasetArrC.forEach((dataset, index) => {
      const isLastDataset = index === datasetArrC.length - 1;
      const datasetData = filterDataByDays(dataset);

      newDataC.datasets.push({
        label: isLastDataset
          ? t("expandedchart.trigger")
          : tSymptom(factorsC[index]),
        data: datasetData,
        pointStyle: isLastDataset ? "triangle" : undefined,
        type: isLastDataset ? "scatter" : undefined,
        backgroundColor: isLastDataset ? theme.primary : colors[index],
        borderColor: isLastDataset ? theme.primary : colors[index],
        borderWidth: 1,
        fill: false,
        hoverBackgroundColor: isLastDataset ? theme.primary : colors[index],
        hoverBorderColor: "white",
      });
    });

    ChartDatasetC = fillInDays(newDataC.datasets, assignedDays);

    newDataC.datasets = ChartDatasetC;
    setAnotherNewData2(newDataC);
  }, [assignedDays, TPOrPatientTrackedData, t]);

  const who = splitView ? `(${patient.caregiverName})` : "";

  // styling for charts
  const extendedChartOption1 = {
    maintainAspectRatio: false,
    title: {
      display: true,
      text: `${assignedDays} ${t("expandedchart.days_in_review")} ${who}`,
      fontFamily: "quicksand",
      fontStyle: "bold",
      fontSize: "20",
      fontColor: dark ? theme.darkModeText : theme.black,
    },
    legend: {
      labels: {
        fontColor: dark ? theme.darkModeText : theme.black,
        usePointStyle: true,
        boxWidth: 8,
      },
    },
  };

  const extendedChartOption2 = {
    maintainAspectRatio: false,
    title: {
      display: true,
      text: `${assignedDays} ${t("expandedchart.days_in_review")} (${
        patient.caregiverName
          ? otherUserInfo.patient_first_name
          : otherUserInfo.tp_first_name
      })`,
      fontFamily: "quicksand",
      fontStyle: "bold",
      fontSize: "20",
      fontColor: dark ? theme.darkModeText : theme.black,
    },
    legend: {
      labels: {
        fontColor: dark ? theme.darkModeText : theme.black,
        usePointStyle: true,
        boxWidth: 8,
      },
    },
  };

  const colorOverRide = {
    scales: {
      xAxes: [
        {
          type: "time",
          time: {
            unit: "day",
            tooltipFormat: "dddd",
          },
          ticks: {
            fontColor: dark ? theme.darkModeText : theme.black,
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            fontColor: dark ? theme.darkModeText : theme.black,
          },
          scaleLabel: {
            display: true,
            labelString: t("expandedchart.intensity_level"),
            fontColor: dark ? theme.darkModeText : theme.black,
          },
        },
      ],
    },
  };

  return (
    <>
      {error && (
        <ComponentWrapper>
          {t("charts.error.0")}
          <SubmitButton onClick={retry}>
            {t("charts.error.1")}
          </SubmitButton>
        </ComponentWrapper>
      )}
      <ComponentWrapper>
        <Row className="d-flex justify-content-around">
          <Form.Group controlId="formBasicSelect">
            <Form.Label>
              {t("expandedchart.days_since")} {startDate}{" "}
            </Form.Label>
            <Form.Control
              as="select"
              // value={date}
              defaultValue={assignedDays}
              onChange={(e) => {
                setAssignedDays(e.target.value);
              }}
            >
              <option
                value={5}
              >{`5 ${t("expandedchart.days_in_review")}`}</option>
              <option
                value={30}
              >{`30 ${t("expandedchart.days_in_review")}`}</option>
              {/* <option value={60}>{`60 ${t("expandedchart.days_in_review")}`}</option> */}
              <option
                value={90}
              >{`90 ${t("expandedchart.days_in_review")}`}</option>
              <option
                value={120}
              >{`120 ${t("expandedchart.days_in_review")}`}</option>
            </Form.Control>
          </Form.Group>

          <SubmitButton
            onClick={() => {
              if (dark) {
                alert(t("common.off_dark_mode_export"));
              } else {
                handleLineModalShow();
              }
            }}
          >
            {t("common.export_pdf")}
          </SubmitButton>
          {user.userType == "provider" && !!userIdForTrackedData && (
            <SubmitButton
              onClick={() => {
                handleSplitViewShow();
              }}
            >
              {t("expandedchart.compare_tracking_data.label")}
            </SubmitButton>
          )}
        </Row>
        <div className="row flex-column justify-content-center align-items-center">
          <div className="row" style={{ marginBottom: "10px" }}>
            {patient.caregiverName &&
              t("expandedchart.report_detail.therapist.0") +
                patient.caregiverName +
                t("expandedchart.report_detail.therapist.1") +
                (patient.patientType ? patient.patientType : "") +
                t("expandedchart.report_detail.therapist.2") +
                patient.firstName +
                " " +
                patient.lastName +
                t("expandedchart.report_detail.therapist.3")}
          </div>

          <div className="row" style={{ marginBottom: "10px" }}>
            {!patient.caregiverName &&
              t("expandedchart.report_detail.patient.0") +
                (patient.patientType ? patient.patientType : "") +
                t("expandedchart.report_detail.patient.1") +
                patient.firstName +
                " " +
                patient.lastName +
                t("expandedchart.report_detail.patient.2")}
          </div>

          {errorMessage && (
            <div style={{ marginBottom: "10px" }}>
              <div style={{ textAlign: "center" }}>{errorMessage}</div>
              {TPList &&
                TPList.map((tp, index) => (
                  <div>
                    <PillButton
                      autoMargin
                      key={index}
                      onClick={() => {
                        handleSubmit(tp.user_id);
                      }}
                    >
                      {tp.first_name + "," + tp.last_name}
                    </PillButton>
                  </div>
                ))}
            </div>
          )}

          {!patient.caregiverName && splitView && !errorMessage && (
            <div className="row" style={{ marginBottom: "10px" }}>
              {t("expandedchart.compare_tracking_data.text.0") +
                otherUserInfo.tp_first_name +
                " " +
                otherUserInfo.tp_last_name +
                t("expandedchart.compare_tracking_data.text.1")}
            </div>
          )}

          {patient.caregiverName && splitView && !errorMessage && (
            <div className="row" style={{ marginBottom: "10px" }}>
              {t("expandedchart.compare_tracking_data.text.0") +
                otherUserInfo.patient_first_name +
                " " +
                otherUserInfo.patient_last_name +
                t("expandedchart.compare_tracking_data.text.1")}
            </div>
          )}
        </div>
      </ComponentWrapper>
      <ModalPdf
        isReport={true}
        show={showLineModal}
        handleClose={handleLineModalClose}
        chart={[
          `trigger-doughnut`,
          `another-trigger-doughnut`,
          `medical-doughnut`,
          `another-medical-doughnut`,
          `trigger-barChart`,
          `another-trigger-barChart`,
          `medical-barChart`,
          "another-medical-barChart",
          `feeling-bar`,
          `another-feeling-bar`,
          `${assignedDays}day-lineChart`,
          `another-${assignedDays}day-lineChart`,
        ]}
        data={data}
        anotherDataForComparasion={TPOrPatientTrackedData}
        additionalNotes={additionalNotes}
        days={assignedDays}
      />
      <Row>
        <Col md={6}>
          <ComponentWrapper>
            <div className={`trigger-doughnut`}>
              <DoughnutTrigger
                splitView={splitView}
                data={triggerData}
                days={assignedDays}
                userName={
                  patient.caregiverName
                    ? patient.caregiverName
                    : patient.firstName
                }
              />
            </div>
          </ComponentWrapper>
        </Col>
        {splitView && !errorMessage && (
          <Col md={6}>
            <ComponentWrapper>
              <div className={`another-trigger-doughnut`}>
                <DoughnutTrigger
                  splitView={splitView}
                  data={anotherTriggerData}
                  days={assignedDays}
                  userName={
                    patient.caregiverName
                      ? otherUserInfo.patient_first_name
                      : otherUserInfo.tp_first_name
                  }
                />
              </div>
            </ComponentWrapper>
          </Col>
        )}
        <Col md={6}>
          <ComponentWrapper>
            <div className={`medical-doughnut`}>
              <DoughnutMedical
                splitView={splitView}
                data={medicalData}
                days={assignedDays}
                userName={
                  patient.caregiverName
                    ? patient.caregiverName
                    : patient.firstName
                }
              />
            </div>
          </ComponentWrapper>
        </Col>

        {splitView && !errorMessage && (
          <Col md={6}>
            <ComponentWrapper>
              <div className={`another-medical-doughnut`}>
                <DoughnutMedical
                  splitView={splitView}
                  data={anotherMedicalData}
                  days={assignedDays}
                  userName={
                    patient.caregiverName
                      ? otherUserInfo.patient_first_name
                      : otherUserInfo.tp_first_name
                  }
                />
              </div>
            </ComponentWrapper>
          </Col>
        )}
      </Row>
      <Row>
        <Col md={6}>
          <ComponentWrapper>
            <div className={`trigger-barChart`}>
              <TriggerBarChart
                splitView={splitView}
                data={triggerByDays}
                userName={
                  patient.caregiverName
                    ? patient.caregiverName
                    : patient.firstName
                }
              />
            </div>
          </ComponentWrapper>
        </Col>
        {splitView && !errorMessage && (
          <Col md={6}>
            <ComponentWrapper>
              <div className={`another-trigger-barChart`}>
                <TriggerBarChart
                  splitView={splitView}
                  data={anotherTriggerByDays}
                  userName={
                    patient.caregiverName
                      ? otherUserInfo.patient_first_name
                      : otherUserInfo.tp_first_name
                  }
                />
              </div>
            </ComponentWrapper>
          </Col>
        )}
        <Col md={6}>
          <ComponentWrapper>
            <div className={`medical-barChart`}>
              <MedicalBarChart
                splitView={splitView}
                data={medicalData}
                userName={
                  patient.caregiverName
                    ? patient.caregiverName
                    : patient.firstName
                }
              />
            </div>
          </ComponentWrapper>
        </Col>
        {splitView && !errorMessage && (
          <Col md={6}>
            <ComponentWrapper>
              <div className={`another-medical-barChart`}>
                <MedicalBarChart
                  splitView={splitView}
                  data={anotherMedicalData}
                  userName={
                    patient.caregiverName
                      ? otherUserInfo.patient_first_name
                      : otherUserInfo.tp_first_name
                  }
                />
              </div>
            </ComponentWrapper>
          </Col>
        )}
      </Row>
      <Row>
        <Col md={4}>
          <ComponentWrapper>
            <div className={`feeling-bar`}>
              <FeelingBar
                splitView={splitView}
                data={medicalData}
                userName={
                  patient.caregiverName
                    ? patient.caregiverName
                    : patient.firstName
                }
              />
            </div>
          </ComponentWrapper>
        </Col>
        <Col md={8}>
          <ComponentWrapper>
            <div className={`${assignedDays}day-lineChart`}>
              <Line
                redraw
                data={newData2}
                width={100}
                height={500}
                options={{
                  ...LineChartOptions,
                  ...extendedChartOption1,
                  ...colorOverRide,
                  tooltips: {
                    ...LineChartOptions.tooltips,
                    callbacks: {
                      ...LineChartOptions.tooltips?.callbacks,
                      title: tooltipCallbacks.title,
                      label: tooltipCallbacks.label,
                    },
                  },
                }}
              />
            </div>
          </ComponentWrapper>
        </Col>

        {splitView && !errorMessage && (
          <Col md={4}>
            <ComponentWrapper>
              <div className={`another-feeling-bar`}>
                <FeelingBar
                  splitView={splitView}
                  data={anotherMedicalData}
                  userName={
                    patient.caregiverName
                      ? otherUserInfo.patient_first_name
                      : otherUserInfo.tp_first_name
                  }
                />
              </div>
            </ComponentWrapper>
          </Col>
        )}

        {splitView && !errorMessage && (
          <Col md={8}>
            <ComponentWrapper>
              <div className={`another-${assignedDays}day-lineChart`}>
                <Line
                  redraw
                  data={anotherNewData2}
                  width={100}
                  height={500}
                  options={{
                    ...LineChartOptions,
                    ...extendedChartOption2,
                    ...colorOverRide,
                    tooltips: {
                      ...LineChartOptions.tooltips,
                      callbacks: {
                        ...LineChartOptions.tooltips?.callbacks,
                        title: tooltipCallbacks.title,
                        label: tooltipCallbacks.label,
                      },
                    },
                  }}
                />
              </div>
            </ComponentWrapper>
          </Col>
        )}
      </Row>
      <SubmitButton onClick={() => history.push("/")}>
        {t("common.go_back")}
      </SubmitButton>
    </>
  );
};

export default ExpandedChart;